(function () {
	'use strict';

	// Function to detect if page is in edit mode (following existing codebase pattern)
	function isEditMode() {
		const isEditorPath = top.location.pathname.includes('/editor.html');
		if (isEditorPath) {
			return 'AUTHOR';
		}
		return 'PUBLISH';
	}

	/**
	 * Initialize text highlight animation for elements within a container
	 * @param {HTMLElement|Document} container - Container to search within (defaults to document)
	 * @param {boolean} isModalContext - Whether this is being called for a modal (skip visibility checks)
	 */
	function initTextHighlightAnimation(container = document, isModalContext = false) {
		if (typeof gsap === 'undefined' || typeof ScrollTrigger === 'undefined') {
			console.warn('GSAP or ScrollTrigger not found. Text highlight animation will not work.');
			return;
		}
		
		// Skip animation initialization if in edit mode
		if (isEditMode() === 'AUTHOR') {
			return;
		}
		
		// iOS and Safari detection
		const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
		const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
		
		const highlightElements = container.querySelectorAll('.animated_text_highlight');
		
		if (highlightElements.length === 0) {
			return; // No elements to animate in this container
		}
		
		// Filter out elements that are inside hidden modals (only on initial page load)
		const visibleElements = [];
		
		highlightElements.forEach((element) => {
			// Skip filtering if this is a modal context (we want all elements in the modal)
			if (isModalContext) {
				visibleElements.push(element);
				return;
			}
			
			// For initial page load, check if element is inside a hidden modal
			const hiddenModal = element.closest('.contentcard-modal, .feature-card-modal');
			if (hiddenModal) {
				const modalStyle = getComputedStyle(hiddenModal);
				if (modalStyle.display === 'none') {
					return; // Skip this element
				}
			}
			
			visibleElements.push(element);
		});
		
		if (visibleElements.length === 0) {
			return; // No visible elements to animate
		}
		
		// iOS-specific fallback using Intersection Observer
		if (isIOS && isSafari) {
			// Use Intersection Observer as fallback for iOS momentum scrolling issues
			// For modal context, use a more generous threshold
			const observerOptions = isModalContext ? {
				root: null,
				rootMargin: '0px 0px -50% 0px', // More generous for modals
				threshold: 0.1
			} : {
				root: null,
				rootMargin: '0px 0px -15% 0px', // Original threshold for page content
				threshold: 0.1
			};
			
			const observer = new IntersectionObserver((entries) => {
				entries.forEach((entry) => {
					if (entry.isIntersecting && !entry.target.classList.contains('active')) {
						entry.target.classList.add('active');
						observer.unobserve(entry.target); // Only trigger once
					}
				});
			}, observerOptions);
			
			visibleElements.forEach((element) => {
				// Only observe elements that haven't been activated yet
				if (!element.classList.contains('active')) {
					// Check if element is already in viewport (common in modals)
					const rect = element.getBoundingClientRect();
					const viewportThreshold = isModalContext ? 0.95 : 0.85; // More generous for modals
					const isInViewport = rect.top >= 0 && rect.top <= window.innerHeight * viewportThreshold;
					
					if (isInViewport && isModalContext) {
						element.classList.add('active');
						return; // Skip observer setup for immediately visible elements
					}
					
					observer.observe(element);
				}
			});
			
		} else {
			// Use ScrollTrigger for non-iOS devices or with additional iOS configurations
			visibleElements.forEach((element) => {
				// Skip elements that already have ScrollTrigger or are already active
				if (element.classList.contains('active') || element._scrollTrigger) {
					return;
				}
				
				// Check if element is already in viewport (common in modals)
				const rect = element.getBoundingClientRect();
				const viewportThreshold = isModalContext ? 0.95 : 0.85; // More generous for modals
				const isInViewport = rect.top >= 0 && rect.top <= window.innerHeight * viewportThreshold;
				
				if (isInViewport && isModalContext) {
					element.classList.add('active');
					return; // Skip ScrollTrigger creation for immediately visible elements
				}
				
				const trigger = ScrollTrigger.create({
					trigger: element,
					start: isModalContext ? 'top 95%' : 'top 85%', // More generous trigger for modals
					onEnter: () => {
						element.classList.add('active');
					},
					once: true,
					// iOS-specific optimizations
					...(isIOS && isSafari && {
						invalidateOnRefresh: true,
						refreshPriority: 0,
						fastScrollEnd: true, // Better handling of quick scrolls
						anticipatePin: false // Disable pin anticipation for smoother iOS performance
					})
				});
				
				// Store reference to ScrollTrigger for cleanup
				element._scrollTrigger = trigger;
			});
		}
	}

	/**
	 * Reinitialize text highlight animation for a specific container (like a modal)
	 * @param {HTMLElement} container - The container element (e.g., modal dialog)
	 * @param {boolean} isModalContext - Whether this is modal/overlay content (defaults to true)
	 */
	function reinitializeTextHighlightAnimation(container, isModalContext = true) {
		if (!container) {
			console.warn('No container provided for text highlight animation reinitialization');
			return;
		}
		
		// Use a small delay to ensure the container is fully rendered and visible
		setTimeout(() => {
			// Find highlight elements and reset their state for fresh processing
			const highlightElements = container.querySelectorAll('.animated_text_highlight');
			
			highlightElements.forEach((element) => {
				// Reset the element state to ensure it gets processed again
				element.classList.remove('active');
				if (element._scrollTrigger) {
					element._scrollTrigger.kill();
					delete element._scrollTrigger;
				}
			});
			
			initTextHighlightAnimation(container, isModalContext);
			
			// Refresh ScrollTrigger to recalculate positions after modal is opened
			if (typeof ScrollTrigger !== 'undefined') {
				ScrollTrigger.refresh();
			}
		}, 100);
	}

	// Initialize on window load (to match header.js pattern)
	window.addEventListener('load', function () {
		initTextHighlightAnimation();
	});
	
	// Additional fallback: reinitialize on visibility change (for iOS tab switching issues)
	document.addEventListener('visibilitychange', function() {
		if (!document.hidden && window.location.pathname.includes('/editor.html') === false) {
			// Small delay to ensure page is ready after tab switch
			setTimeout(() => {
				// Refresh ScrollTrigger if available
				if (typeof ScrollTrigger !== 'undefined') {
					ScrollTrigger.refresh();
				}
			}, 100);
		}
	});

	// Make the reinitialize function globally available for modal components
	window.reinitializeTextHighlightAnimation = reinitializeTextHighlightAnimation;
})();